/**
 * Codes individuels — base 32 de Crockford (SPECIFICATION.md §7.1).
 *
 * L'alphabet exclut I, L, O et U : aucune confusion possible entre 1 et I,
 * entre 0 et O. Un caractère de contrôle (modulo 37) détecte les fautes de
 * saisie, ce qui évite qu'un élève soit compté nul pour une simple coquille.
 */

import { sha256 } from './hash.js';
import { randomInt } from './random.js';

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const CHECK_SYMBOLS = ['*', '~', '$', '=', 'U'] as const;

/** Longueurs admises par usage : symboles de données, hors contrôle. */
export const CODE_LENGTHS = {
  /** Code élève : 8 symboles de données + 1 caractère de contrôle. */
  eleve: 8,
  /** Code enseignant : 12 symboles de données + 1 caractère de contrôle. */
  enseignant: 12,
} as const;

/** Valeur d'un symbole de données, ou -1 s'il est hors alphabet. */
export function symbolValue(symbol: string): number {
  return ALPHABET.indexOf(symbol);
}

/**
 * Caractère de contrôle d'une suite de symboles de données.
 * Somme des valeurs modulo 37, projetée sur l'alphabet puis sur les
 * symboles de contrôle, conformément à la spécification Crockford.
 */
export function checkSymbol(data: string): string {
  let sum = 0;
  for (const symbol of data) {
    const value = symbolValue(symbol);
    if (value < 0) {
      throw new Error(`Symbole hors alphabet Crockford : ${symbol}`);
    }
    sum += value;
  }
  const remainder = sum % 37;
  if (remainder < 32) return ALPHABET[remainder] as string;
  return CHECK_SYMBOLS[remainder - 32] as string;
}

/** Tire `length` symboles de données aléatoires, sans biais modulo. */
export function randomSymbols(length: number): string {
  let out = '';
  for (let i = 0; i < length; i += 1) {
    out += ALPHABET[randomInt(32)] as string;
  }
  return out;
}

/**
 * Normalise une saisie humaine : majuscules, suppression des séparateurs,
 * et levée des ambiguïtés classiques (I et L vers 1, O vers 0).
 */
export function normalizeCode(input: string): string {
  let out = '';
  for (const rawChar of input.toUpperCase()) {
    const char = rawChar === 'I' || rawChar === 'L' ? '1' : rawChar === 'O' ? '0' : rawChar;
    if (ALPHABET.includes(char) || (CHECK_SYMBOLS as readonly string[]).includes(char)) {
      out += char;
    }
  }
  return out;
}

/** Découpe un code en groupes lisibles : `XXXXX-XXXX`. */
export function formatCode(raw: string, groupSize = 5): string {
  const groups: string[] = [];
  for (let i = 0; i < raw.length; i += groupSize) {
    groups.push(raw.slice(i, i + groupSize));
  }
  return groups.join('-');
}

export interface GeneratedCode {
  /** Code complet normalisé, contrôle inclus : c'est ce qui est imprimé. */
  readonly raw: string;
  /** Même code, découpé pour la lecture humaine. */
  readonly display: string;
}

/** Fabrique un code complet (données + contrôle). */
export function generateCode(dataLength: number): GeneratedCode {
  const data = randomSymbols(dataLength);
  const raw = `${data}${checkSymbol(data)}`;
  return { raw, display: formatCode(raw) };
}

/** Vérifie la longueur et le caractère de contrôle d'un code saisi. */
export function isValidCode(input: string, dataLength: number): boolean {
  const raw = normalizeCode(input);
  if (raw.length !== dataLength + 1) return false;
  const data = raw.slice(0, dataLength);
  const control = raw.slice(dataLength);
  try {
    return checkSymbol(data) === control;
  } catch {
    return false;
  }
}

/**
 * Empreinte d'un code, salée par l'élection (SPECIFICATION.md §7.2).
 * Le code en clair n'est jamais journalisé : seule cette empreinte l'est.
 */
export function tokenHash(rawCode: string, salt: string): string {
  return sha256(`${salt}:${normalizeCode(rawCode)}`).slice(0, 32);
}
